import { useCallback, useMemo, useState } from 'react';

import type {
  GetSentimentDataQuery,
  TalentDiscoveryRespUserV3,
} from '@revelio/data-access';

import type { CareerMoveStep } from '../optimize/CareerMovesInsight';
import { useCompanyRics } from './useCompanyRics';
import {
  type CompanyMetrics,
  useCompetitorComposition,
} from './useCompetitorComposition';
import { type Competitor, useCompetitors } from './useCompetitors';
import { type EmployerHealth, useEmployerHealth } from './useEmployerHealth';
import { type HiddenGem, useHiddenGems } from './useHiddenGems';
import { usePostingsComparison } from './usePostingsComparison';
import type { ResolvedProfileIds } from './useResolveProfileIds';
import {
  type CompanyLeverageSignal,
  useRolePostingSignals,
} from './useRolePostingSignals';
import {
  type CompanySentiment,
  useSentimentComparison,
} from './useSentimentComparison';
import { useSentimentEffects } from './useSentimentEffects';
import {
  useWorkHistoryCompanies,
  type WorkHistoryCompany,
} from './useWorkHistoryCompanies';

export interface EmployerIntelligenceResult {
  // Work history
  workHistoryCompanies: WorkHistoryCompany[];

  // Competitor source selection
  competitorSourceOverride: number | null;
  handleCompetitorSourceChange: (rcid: number | null) => void;

  // Competitors
  competitors: Competitor[];
  competitorsLoading: boolean;
  isIndustryFallback: boolean;

  // Metrics, sentiments, postings
  metrics: CompanyMetrics[];
  sentiments: CompanySentiment[];

  // Employer health
  healthData: EmployerHealth[];
  currentEmployerHealth: EmployerHealth | undefined;

  // Hidden gems
  hiddenGems: HiddenGem[];

  // Leverage signals
  leverageSignals: CompanyLeverageSignal[];
  leverageRoleName: string | null;
  signalsLoading: boolean;
  signalsError: string | null;

  // Sentiment effects (for current employer)
  sentimentEffectsData: GetSentimentDataQuery | undefined;
  sentimentEffectsLoading: boolean;

  // Derived values
  currentCompanyMetrics: CompanyMetrics | undefined;
  careerMoveSteps: CareerMoveStep[];

  // Loading states
  isDataLoading: boolean;
  isTableLoading: boolean;
}

/**
 * Composite hook that encapsulates all employer intelligence data fetching
 * and derived computations. Combines work history analysis, competitor
 * discovery, sentiment/postings comparison, employer health scoring,
 * hidden gems identification, and leverage signals.
 */
export const useEmployerIntelligence = (
  currentUser: TalentDiscoveryRespUserV3 | null,
  resolvedIds: ResolvedProfileIds
): EmployerIntelligenceResult => {
  // ── Work history ──

  const { companies: workHistoryCompanies } =
    useWorkHistoryCompanies(currentUser);

  const allHistoricalRcids = useMemo(
    () =>
      workHistoryCompanies
        .filter((c) => !c.isCurrent && c.rcid > 0)
        .map((c) => c.rcid),
    [workHistoryCompanies]
  );

  // ── Competitor source selection ──

  const [competitorSourceOverride, setCompetitorSourceOverride] = useState<
    number | null
  >(null);
  const competitorSourceRcid =
    competitorSourceOverride ?? resolvedIds.companyRcid;

  const handleCompetitorSourceChange = useCallback((rcid: number | null) => {
    setCompetitorSourceOverride(rcid);
  }, []);

  const competitorSourceName = useMemo(() => {
    if (!competitorSourceOverride) return resolvedIds.companyName;
    const match = workHistoryCompanies.find(
      (c) => c.rcid === competitorSourceOverride
    );
    return match?.companyName ?? null;
  }, [competitorSourceOverride, workHistoryCompanies, resolvedIds.companyName]);

  const { rics: competitorSourceRics } = useCompanyRics(
    competitorSourceName,
    competitorSourceRcid
  );

  const competitorFallbackFilters = useMemo(() => {
    if (
      !competitorSourceRics.ricsK10 &&
      !competitorSourceRics.ricsK50 &&
      !competitorSourceRics.ricsK400
    ) {
      return null;
    }
    return { rics: competitorSourceRics };
  }, [competitorSourceRics]);

  // ── Competitors ──

  const {
    competitors,
    isLoading: competitorsLoading,
    isIndustryFallback,
  } = useCompetitors(competitorSourceRcid, 15, competitorFallbackFilters);

  // ── Composition, sentiment, postings ──

  const { metrics, isLoading: compositionLoading } = useCompetitorComposition(
    competitors,
    resolvedIds,
    allHistoricalRcids
  );

  const { sentiments, isLoading: sentimentLoading } = useSentimentComparison(
    competitors,
    resolvedIds,
    allHistoricalRcids
  );

  const { postings, isLoading: postingsLoading } = usePostingsComparison(
    competitors,
    resolvedIds,
    allHistoricalRcids
  );

  // ── Role posting signals ──

  const emptyCompanyIds = useMemo(() => [] as number[], []);
  const {
    signals: leverageSignals,
    roleName: leverageRoleName,
    isLoading: signalsLoading,
    error: signalsError,
  } = useRolePostingSignals(
    competitors,
    resolvedIds,
    resolvedIds.roleLevels,
    resolvedIds.seniorityId,
    emptyCompanyIds,
    null
  );

  // ── Employer health ──

  const isDataLoading =
    compositionLoading || sentimentLoading || postingsLoading;

  const { healthData } = useEmployerHealth(
    metrics,
    sentiments,
    postings,
    isDataLoading
  );

  // ── Hidden gems ──

  const allHistoricalRcidSet = useMemo(
    () => new Set(allHistoricalRcids),
    [allHistoricalRcids]
  );

  const { hiddenGems } = useHiddenGems(
    metrics,
    sentiments,
    competitors,
    resolvedIds.companyRcid,
    allHistoricalRcidSet
  );

  // ── Sentiment effects (current employer) ──

  const { data: sentimentEffectsData, isLoading: sentimentEffectsLoading } =
    useSentimentEffects(resolvedIds.companyRcid);

  // ── Derived values ──

  const currentEmployerHealth = useMemo(
    () => healthData.find((h) => h.companyId === resolvedIds.companyRcid),
    [healthData, resolvedIds.companyRcid]
  );

  const currentCompanyMetrics = useMemo(
    () => metrics.find((m) => m.companyId === resolvedIds.companyRcid),
    [metrics, resolvedIds.companyRcid]
  );

  const careerMoveSteps = useMemo((): CareerMoveStep[] => {
    return workHistoryCompanies.map((company) => {
      const health = healthData.find((h) => h.companyId === company.rcid);
      const sentiment = sentiments.find((s) => s.companyId === company.rcid);

      return {
        rcid: company.rcid,
        companyName: company.companyName,
        title: company.title,
        startDate: company.startDate,
        endDate: company.endDate,
        salary: company.salary,
        stabilityScore: health?.stabilityScore ?? null,
        risk: health?.risk ?? null,
        sentimentOverall: sentiment?.scores.overall ?? null,
      };
    });
  }, [workHistoryCompanies, healthData, sentiments]);

  const isTableLoading = competitorsLoading || compositionLoading;

  return {
    workHistoryCompanies,
    competitorSourceOverride,
    handleCompetitorSourceChange,
    competitors,
    competitorsLoading,
    isIndustryFallback,
    metrics,
    sentiments,
    healthData,
    currentEmployerHealth,
    hiddenGems,
    leverageSignals,
    leverageRoleName,
    signalsLoading,
    signalsError,
    sentimentEffectsData,
    sentimentEffectsLoading,
    currentCompanyMetrics,
    careerMoveSteps,
    isDataLoading,
    isTableLoading,
  };
};
